import React, { useMemo, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import BaseTableCard, {
  buildTableCardItems,
} from 'components/atoms/base-table-card';
import { useRunbookLog } from 'infrastructure/hooks/clinic/use-runbook-logs';
import { DateFormatter } from 'infrastructure/functions';
import BaseSpinWrapper from 'components/atoms/base-spin-wrapper';
import classNames from 'classnames';
import RunBookCompareContent from 'components/organisms/runbooks/components/runbook-compare-content';
import BaseButton from 'components/atoms/baseButton';
import ComparerButton from 'components/atoms/icons/comparerButton';
import {
  ClinicDoNotCallRestrictionType,
  DateFormats,
} from 'infrastructure/enums';
import { doNotCallRestrictionTypeDataSource } from 'infrastructure/data-sources/do-not-call-restriction-types';
import { timesDataSource } from 'infrastructure/data-sources/times';

import s from './styles.module.scss';

import type { Clinic } from 'infrastructure/classes/clinic';

const checkValue = (v?: string | null) => v || '-';

interface IProps {
  clinicGuid: string | undefined;
  info: Clinic;
}

const ClinicInstructionsCard: React.FC<IProps> = (props) => {
  const { clinicGuid, info } = props;

  const [comparerOpened, toggleOpenComparer] = useReducer((v) => !v, false);
  const { t } = useTranslation();
  const { runbookLogs, loading } = useRunbookLog({
    meta: {
      clinicGuid,
      items: 100,
    },
  });

  const [instructionData, ...history] = runbookLogs?.items ?? [];

  const rTypes = doNotCallRestrictionTypeDataSource({ type: 'clinic' });
  const times = timesDataSource();

  const clinicInstructions = useMemo(() => {
    const buildDoNotCallInfo = (
      prefix: 'RPM' | 'CCM',
      restrictionType?: ClinicDoNotCallRestrictionType | null,
      startTime?: string | null,
      endTime?: string | null,
    ) => {
      const items = [
        {
          label: `${prefix} ${t('labels.doNotCallRestrictionType')}`,
          value: checkValue(rTypes.findByField('id', restrictionType)?.name),
        },
      ];

      if (restrictionType === ClinicDoNotCallRestrictionType.LimitedHours) {
        items.push(
          {
            label: `${prefix} ${t('labels.doNotCallStartTime')}`,
            value: checkValue(times.findById(startTime)?.name),
          },
          {
            label: `${prefix} ${t('labels.doNotCallEndTime')}`,
            value: checkValue(times.findById(endTime)?.name),
          },
        );
      }

      return items;
    };

    const rpmInfo = buildDoNotCallInfo(
      'RPM',
      info?.doNotCallRestrictionType,
      info?.doNotCallStartTime,
      info?.doNotCallEndTime,
    );

    const ccmInfo = buildDoNotCallInfo(
      'CCM',
      info?.ccmDoNotCallRestrictionType,
      info?.ccmDoNotCallStartTime,
      info?.ccmDoNotCallEndTime,
    );

    return buildTableCardItems([
      [
        {
          label: t('labels.criticalBusinessHours'),
          value: checkValue(instructionData?.criticalBusinessHours),
          multiply: true,
          originView: true,
          htmlView: true,
        },
        {
          label: t('labels.criticalAfterHours'),
          value: checkValue(instructionData?.criticalAfterHours),
          multiply: true,
          originView: true,
          htmlView: true,
        },
      ],
      [
        {
          label: t('labels.businessHours'),
          value: checkValue(instructionData?.businessHours),
        },
        {
          label: t('labels.afterHours'),
          value: checkValue(instructionData?.afterHours),
        },
        {
          label: t('labels.contactMethod'),
          value: checkValue(instructionData?.contactMethod),
        },
        {
          label: t('labels.contactMethodValue'),
          value: checkValue(instructionData?.contactMethodValue),
        },
        {
          label: t('labels.dateNote'),
          value: checkValue(instructionData?.dateNoteLogged),
        },
        {
          label: t('labels.note'),
          value: checkValue(instructionData?.notes),
          multiply: true,
          originView: true,
          htmlView: true,
        },
      ],
      [...rpmInfo, ...ccmInfo],
    ]);
  }, [instructionData, info]);

  const updateDateLabel = `${t('labels.updateDate')}: ${DateFormatter({
    date: instructionData?.createdAt,
    format: DateFormats['MM-dd-yyyy | h:mm a'],
  })}`;

  const controls = instructionData ? (
    <div className={classNames(s.controls)}>
      <span>{updateDateLabel}</span>
      <BaseButton
        type="secondary"
        className={s['comparer-button']}
        onClick={toggleOpenComparer}
        icon={<ComparerButton />}
      />
    </div>
  ) : null;

  return (
    <>
      <BaseSpinWrapper spinning={loading}>
        <BaseTableCard
          title={t('labels.clinicInstructions')}
          controls={controls}
          columns={2}
          rows={[1, 5, 3]}
          items={clinicInstructions}
        />
      </BaseSpinWrapper>
      {comparerOpened && (
        <RunBookCompareContent
          comparerOpened
          setComparerOpened={toggleOpenComparer}
          currentData={instructionData}
          history={history}
        />
      )}
    </>
  );
};

export default ClinicInstructionsCard;
